/* eslint-disable i18next/no-literal-string */
import { useApiMutation } from '@/hooks/useApi';
import { Box, Button } from '@mui/material';
import { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import PageHeader from '@/pages/staff/components/PageHeader';
import SearchCard from '@/pages/staff/components/SearchCard';
import SearchTabs from '@/pages/staff/components/SearchTabs';
import StaffDataTable from '@/pages/staff/components/StaffDataTable';
import StaffSearchForm from '@/pages/staff/components/StaffSearchForm';
import TabPanel from '@/pages/staff/components/TabPanel';
import TeamSearchForm from '@/pages/staff/components/TeamSearchForm';
// import { fullName } from '@/util/util';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
// Define the data structure for a staff record - same as in StaffDataTable
interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  agentId: string;
  uuid: string;
  email: string;
  status: 'Active' | 'Inactive';
  teamType?: string;
  teamName?: string;
  teamLead: boolean;
  state: string;
  country: string;
}

const StaffPage = () => {
  const { t } = useTranslation(['auth']);
  const { logout } = useAuth();
  const { authState } = useOktaAuth();
  const [tabValue, setTabValue] = useState(0);
  const [searchParams, setSearchParams] = useState({
    email: '',
    uuid: '',
    firstName: '',
    lastName: '',
  });
  const [searchResults, setSearchResults] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autoLoadAgent, setAutoLoadAgent] = useState<{
    uuid: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  } | null>(null);

  useEffect(() => {
    if (!authState?.isAuthenticated) return;
    const raw = sessionStorage.getItem('pendingStaffAgent');
    if (!raw) return;
    sessionStorage.removeItem('pendingStaffAgent');
    try {
      const parsed = JSON.parse(atob(decodeURIComponent(raw)));
      if (parsed?.uuid) {
        setAutoLoadAgent(parsed);
      }
    } catch {
      // malformed payload — ignore and show normal search
    }
  }, [authState?.isAuthenticated]);

  const { mutateAsync: getAgents } = useApiMutation({
    // baseURL: process.env.REACT_APP_API_URL || '',
    url: '/staff/agents',
    method: 'post',
    endpoint: 'prod',
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Only for the "identifier" field (UUID or Email)
    if (name === 'identifier') {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      setSearchParams((prev) => ({
        ...prev,
        email: isEmail ? value : '',
        uuid: !isEmail ? value : '',
      }));
    } else {
      setSearchParams((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleClear = () => {
    setSearchParams({
      email: '',
      uuid: '',
      firstName: '',
      lastName: '',
    });
    // Don't clear the search results - just reset the form
  };

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    // const name = fullName();

    try {
      // Call the API with the search parameters
      const response: any = await getAgents({
        email: searchParams.email || null,
        uuid: searchParams.uuid || null,
        firstName: searchParams.firstName || null,
        lastName: searchParams.lastName || null,
      });
      setSearchResults(response.value);
    } catch (error) {
      setSearchResults([]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  const handleLogoutFun = async () => {
    await logout();
  };
  return (
    <Box
      data-testid="staff-page"
      sx={{
        minHeight: '100vh',
        pt: 8,
        pb: 12,
        width: '100%',
      }}
    >
      <PageHeader data-testid="staff-page-header" />

      <Box
        data-testid="search-tabs-container"
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <SearchTabs
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          data-testid="staff-search-tabs"
        />
      </Box>

      <TabPanel value={tabValue} index={0} data-testid="staff-tab-panel">
        <StaffSearchForm
          searchParams={searchParams}
          handleInputChange={handleInputChange}
          handleSearch={handleSearch}
          handleClear={handleClear}
          onKeyDown={handleKeyDown}
          data-testid="staff-search-form"
        />

        {(hasSearched || searchResults.length > 0 || autoLoadAgent) && (
          <StaffDataTable
            data={searchResults}
            loading={loading}
            autoLoadAgent={autoLoadAgent}
            onAutoLoadDone={() => setAutoLoadAgent(null)}
            data-testid="staff-data-table"
          />
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1} data-testid="teams-tab-panel">
        <SearchCard disabled data-testid="teams-search-card">
          <TeamSearchForm data-testid="team-search-form" />
        </SearchCard>
      </TabPanel>
      <Button
        variant="contained"
        onClick={handleLogoutFun}
        data-testid="logout-button"
        sx={{
          position: 'fixed',
          borderRadius: '8px',
          top: '10px',
          right: '10px',
        }}
      >
        {t('logOut')}
        <LogoutIcon sx={{ marginLeft: '8px' }} data-testid="logout-icon" />
      </Button>
    </Box>
  );
};

export default StaffPage;
